import * as React from 'react';
import { Link } from 'react-router-dom';
import { AppServices } from '../../../AppServices';
import { ReportContext } from '../../../store/Models/Customers/Customer';
import { ReportTypesManager } from '../../../store/Managers/ReportTypesManager';
import Upsell from '../../Upsell/Upsell';
import { ToxnotReactComponentPaths } from '../ToxnotReactComponentPaths';
import { UpsellProBadge } from '../Upsell';
import { RegulationManager } from '../../../store/Managers/RegulationManager';
import { DynamicRegulation } from '../../../store/Models/Compliance/DynamicRegulation';

interface ReportTypeSelectorRenderOptions {
    onlyAllowSingleSelection: boolean;
    showListInput: boolean;
    showRegulations: boolean;
    initialSelectedReportsTypes?: { [reportType: string]: ReportContext | null };
}

interface ReportTypeSelectorProps {
    appServices: AppServices;
    reportTypesAndContexts: {[reportType: string]: ReportContext | null};
    reportSelectedCallback: (selectedReports: { [reportType: string]: ReportContext | null }, reportTypesAndRegulationsToSpecificInfo: { [reportTypeOrRegulation: string]: string }) => void;
    renderOptions: ReportTypeSelectorRenderOptions;
    isFreeUser: boolean;
    dynRegs: DynamicRegulation[];
}

interface ReportTypeSelectorState {
    selectedReports: { [reportType: string]: ReportContext | null }
    reportTypesAndRegulationsToSpecificInfo: { [reportTypeOrRegulation: string]: string }
}

// ===============================================================================

export class ReportTypeSelector extends React.PureComponent<ReportTypeSelectorProps, ReportTypeSelectorState> {

    // ===============================================================================

    constructor(props: ReportTypeSelectorProps) {
        super(props);

        this.state = {
            selectedReports: props.renderOptions.initialSelectedReportsTypes ?? {},
            reportTypesAndRegulationsToSpecificInfo: {}
        }
    }

    // ===============================================================================

    public render() {
        let reportTypes = Object.keys(this.props.reportTypesAndContexts);

        return (
            <div className="items-selection-container">
                {reportTypes.map((r, index) => (
                    <div key={index} className="checkbox">
                        <label>
                            <input className="rts-report-type-checkbox" type="checkbox" onChange={e => this.reportTypeCheckbox_onChange(e, r, this.props.reportTypesAndContexts[r])} checked={this.isReportChecked(r)} disabled={this.shouldShowUpsell(r)} /> {ReportTypesManager.getFriendlyNameForReportTypeOrRegulation(r, this.props.appServices, this.props.dynRegs)} {this.shouldShowUpsell(r) && <UpsellProBadge navigateToUpgradePage={true} />}
                            {this.props.renderOptions.showRegulations && this.renderRegulationsFromContext(r, this.props.reportTypesAndContexts[r])}
                        </label>
                    </div>
                ))}
            </div>
        );
    }

    // ===============================================================================

    private shouldShowUpsell(reportType: string) {
        return this.props.isFreeUser && !ReportTypesManager.freeAccountReportTypes.includes(reportType);
    }

    // ===============================================================================

    private renderRegulationsFromContext(reportType: string, context: ReportContext | null) {
        if (context === null) {
            return null;
        }

        const regulations = this.props.dynRegs.filter(d => context.regulations.includes(d.regulationId)).orderBy(d => d.config.name);

        return regulations.map((reg, index) => (
            reg.regulationId === RegulationManager.SaintGobainGenericRegulationId ?
            this.renderGenericReportingStatement(reportType, reg.regulationId, index) :
            <div key={index} className="checkbox">
                <label>
                    <input type="checkbox" onChange={e => this.regulationCheckbox_onChange(e, reportType, reg.regulationId)} checked={this.isRegulationChecked(reportType, reg.regulationId)} /> {reg.config.name}
                </label>
            </div>
        ));
    }

    // ===============================================================================

    private renderGenericReportingStatement(reportType: string, regulation: string, index: number) {
        let body: JSX.Element;

        if (this.props.renderOptions.showListInput) {
            body = (
                <div key={index} className="generic-report-type-container">
                    <p>
                        {this.props.appServices.localizeString("Request screening against a list (Optional)")}
                    </p>
                    <div className="report-type-checkbox-inputs-container">
                        {<input type="text" onChange={e => this.reportTypeOrRegulationInput_onChange(e, reportType, regulation)} className="form-control" placeholder={this.props.appServices.localizeString("List name to screen against")} />}
                    </div>
                </div>
            );
        }

        else {
            body = (
                <div key={index} className="checkbox">
                    <label className="report-type-checkbox-inputs-container">
                        <input type="checkbox" onChange={e => this.regulationCheckbox_onChange(e, reportType, regulation)} checked={this.isRegulationChecked(reportType, regulation)} /> {ReportTypesManager.getFriendlyNameForReportTypeOrRegulation(regulation, this.props.appServices, this.props.dynRegs)}
                    </label>
                </div>
            );
        }

        return (
            body
        );
    }

    // ===============================================================================

    private isRegulationChecked(reportType: string, regulation: string) {
        if (!this.state.selectedReports[reportType]) {
            return false;
        }

        if (this.state.selectedReports[reportType] === null) {
            return;
        }

        return this.state.selectedReports[reportType]!.regulations.some(r => r === regulation);
    }

    // ===============================================================================

    private isReportChecked(reportType: string) {
        return this.state.selectedReports[reportType] !== undefined;
    }

    // ===============================================================================

    /*
     * Creates and returns the currently selected report types and regulations. Called when a regulation
     * is changed. DOES NOT update the state. The caller must do the updating of the state.
    */
    private createNewSelectedReports(add: boolean, reportType: string, regulation: string) {
        const newSelectedItems = { ...this.state.selectedReports };

        let context = newSelectedItems[reportType];

        if (add) {
            if (!context) {
                context = { regulations: [], listId: null };
                newSelectedItems[reportType] = context;
            }

            context.regulations = context.regulations.filter(r => r !== regulation);
            context.regulations.push(regulation);
        }

        else {
            if (context === null) {
                return newSelectedItems;
            }

            context.regulations = context.regulations.filter(r => r !== regulation);

            if (context.regulations.length === 0) {
                delete newSelectedItems[reportType];
            }
        }

        return newSelectedItems;
    }

    // ===============================================================================
    //
    // UI Events
    //

    private reportTypeCheckbox_onChange = (e: React.ChangeEvent<HTMLInputElement>, reportType: string, context: ReportContext | null) => {
        const newSelectedItems = this.props.renderOptions.onlyAllowSingleSelection ? {} : { ...this.state.selectedReports }; 

        if (e.target.checked) {
            newSelectedItems[reportType] = context === null ? null : {
                ...context,
                regulations: this.props.renderOptions.showListInput ? context.regulations.filter(r => r !== RegulationManager.SaintGobainGenericRegulationId) : context.regulations
            };
        }

        else {
            delete newSelectedItems[reportType];
        }

        this.setState({
            selectedReports: newSelectedItems
        }); 

        this.props.reportSelectedCallback(newSelectedItems, this.state.reportTypesAndRegulationsToSpecificInfo);
    }

    // ===============================================================================

    private regulationCheckbox_onChange = (e: React.ChangeEvent<HTMLInputElement>, reportType: string, regulation: string) => {
        const newSelectedItems = this.createNewSelectedReports(e.target.checked, reportType, regulation);

        this.setState({
            selectedReports: newSelectedItems
        });

        this.props.reportSelectedCallback(newSelectedItems, this.state.reportTypesAndRegulationsToSpecificInfo);
    }

    // ===============================================================================

    private reportTypeOrRegulationInput_onChange = (e: React.ChangeEvent<HTMLInputElement>, reportType: string, regulation: string) => {
        const newSelectedItems = this.createNewSelectedReports(e.target.value !== "", reportType, regulation);

        const newInfo = { ...this.state.reportTypesAndRegulationsToSpecificInfo };

        newInfo[reportType] = e.target.value;

        this.setState({
            reportTypesAndRegulationsToSpecificInfo: newInfo,
            selectedReports: newSelectedItems
        });

        this.props.reportSelectedCallback(newSelectedItems, newInfo);
    }
}
